import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { API_ENDPOINTS } from '../../../../utils/api';
import createAxiosInstance from "../../../../utils/axiosInstance";
import CollaborativeInfo from '../../Common/CollaborativeInfo';
import Loader from '../../../../Components/Loader';
import {
  GoogleMap,
  InfoWindowF,
  MarkerF,
  useLoadScript
} from "@react-google-maps/api";
import { Agency, AgencyWithLocation } from '../../../../type/Agency';
import PushPin from "../../../../assets/images/pushpin.svg";
import PushPinRefferal from "../../../../assets/images/pushpin-refferal.svg";
import PushPinShelter from "../../../../assets/images/pushpin-shelter.svg";
import ViewAgencyModelInfo from '../../Common/ViewAgencyModelInfo';

const CollaborativeDetails = () => {
  const { groupId } = useParams();
  const axiosInstance = createAxiosInstance();

  const [membersList, setMembersList] = useState<any>({});
  const [locations, setLocation] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [perticularAgency, setParticularAgency] = useState<Agency | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const modalDiv = document.createElement("div");
  const [openInfo, setOpenInfo] = useState<string | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [center, setCenter] = useState({
      lat: 37.7749,
      lng: -122.4194,
  });

  useEffect(() => {
    getMembersList(groupId ?? "unknown");
  }, []);

  const getMembersList = async (groupId: string) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.BEDFINDER.GETMEMBERLIST.replace("{collaborativeId}", groupId));

      const data = response.data; // Extract the actual data
      
      const locationData = data.agencies.filter(
        (agency: any): agency is AgencyWithLocation =>
          !!agency.profile?.location &&
          agency.profile?.location != "__private__" &&
          typeof agency.profile?.location.lat == "number" &&
          typeof agency.profile?.location.lng == "number",
      );

      setMembersList({ name: data.name, agencies: data.agencies, groupId: data.groupId });
      setLocation(locationData);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  }

  const { isLoaded } = useLoadScript({
      googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
  });

  const iconSelector = (agency: Agency) => {
    const { regionalSupporter, canAnswerRequest, canSendRequest } =
    agency.groupInfo?.find(groupInfo => groupInfo?.groupId === membersList.groupId) || {};
    const icon = {
    anchor: new google.maps.Point(12, 12),
    scaledSize: new google.maps.Size(24, 24),
    };
    if (!canAnswerRequest && canSendRequest) {
    return {
        ...icon,
        url: PushPinRefferal,
    };
    }
    if (canAnswerRequest) {
    return {
        ...icon,
        url: PushPinShelter,
    };
    }
    if (regionalSupporter) {
    return {
        ...icon,
        url: PushPinRefferal,
    };
    }
    return {
    ...icon,
    url: PushPin,
    };
  };

  const fitToCoordinates = (
    map: google.maps.Map,
    coordinates: google.maps.LatLngLiteral[],
    options: {
    animated?: boolean;
    edgePadding?: google.maps.Padding;
    } = {},
  ) => {
    const bounds = new google.maps.LatLngBounds();

    if (coordinates.length > 1) {
    coordinates.forEach(coord => bounds.extend(coord));
    (options.animated ? map.panToBounds : map.fitBounds).call(
        map,
        bounds,
        options.edgePadding,
    );
    } else if (coordinates.length == 1) {
    map.panTo(coordinates[0]);
    }
  };

  const handleMapMounted = (map: google.maps.Map) => {
    map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(modalDiv);
    setMap(map);
    fitToCoordinates(
    map,
    locations.map((it: any) => it.profile.location),
    );
  };

  const onMapClick = () => {
      setOpenInfo(null);
  };

  const onDragEnd = () => {
      setCenter(map?.getCenter()?.toJSON() ?? center);
  };

  const onViewMap = (agency: AgencyWithLocation) => {
    try {
      document.getElementById("map")?.scrollIntoView();
    } catch (err) {
      window.scrollTo(0, 0);
    }
    map?.panTo(agency.profile.location);
    setOpenInfo(agency.agencyId);
  };
  
  const viewInfo = (agency: Agency) => () => {
      // Exit Full Screen Mode
      if (document.fullscreenElement) {
      document.exitFullscreen();
      }
      setParticularAgency(agency);
      setIsModalOpen(true); 
      /* else if (document.mozFullScreenElement) {
      document.mozCancelFullScreen();
      } else if (document.webkitFullscreenElement) {
      document.webkitExitFullscreen();
      } else if (document.msFullscreenElement) {
      document.msExitFullscreen();
      }*/

      // if (typeof onInfoPopup == "function") {
      //     onInfoPopup(agency, document.fullscreenElement ? modalDiv : undefined);
      // } else {
      //     try {
      //         document.getElementById(agency.agencyId)?.scrollIntoView();
      //     } catch (err) {
      //         // noop
      //     }
      // }
  };

  const closeViewAgencyModel = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      {isLoading && <Loader />}
      <div className='bg-white'>
        <div className="container py-4 md:py-10 2xl:py-[70px]">
          <div className="mb-4">
            <div className="flex justify-between">
              <div className="text">
                <h3 className="text-gray-900 font-semibold text-base lg:text-lg 2xl:text-xl mb-2">{membersList?.name}</h3>
                <p><span className='text-primary text-base font-medium'>{membersList?.agencies?.length} {membersList?.agencies?.length > 1 ? "agencies" : "agency"}</span> Safe Shelter Collaborative Hub</p>
              </div>
              <Link to={'/bedfinder/collaborative'} className="btn btn-outline-primary px-4 text-sm py-2 h-fit font-medium min-w-24">View All Hubs</Link>
            </div>
          </div>
          <div className="mb-4 flex h-[350px] overflow-hidden rounded-lg">
          {isLoaded && locations && (
              <GoogleMap
                  id="map"
                  mapContainerStyle={{
                      width: "100%",
                      height: "100%",
                  }}
                  options={{
                      streetViewControl: false,
                      mapTypeControl: false,
                      maxZoom: 10,
                  }}
                  center={center}
                  zoom={10}
                  onLoad={handleMapMounted}
                  onClick={onMapClick}
                  onDragEnd={onDragEnd}
              >
                  {locations.map((agency: any) => (
                      <MarkerF
                      key={agency.agencyId}
                      position={agency.profile.location}
                      icon={iconSelector(agency)}
                      onClick={() => setOpenInfo(agency.agencyId)}
                      >
                      {openInfo == agency.agencyId && (
                          <InfoWindowF onCloseClick={() => setOpenInfo(null)}>
                          <div>
                              <h5>{agency.name}</h5>
                              <p>
                              {agency.details?.city}, {agency.details?.state}
                              {agency.profile.county != "__private__" && (
                                  <>
                                  <br />
                                  <span>{agency.profile.county}</span>
                                  </>
                              )}
                              </p>

                              <p>
                                <a className='text-secondary font-semibold cursor-pointer' onClick={viewInfo(agency)}>View info</a>
                              </p>
                          </div>
                          </InfoWindowF>
                      )}
                      </MarkerF>
                  ))}
              </GoogleMap>
            )}
            {/* <iframe className='rounded-xl' title='mapId' src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d202006.19207635472!2d-122.55288465597!3d37.71153795937665!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80859a6d00690021%3A0x4a501367f076adff!2sSan%20Francisco%2C%20CA%2C%20USA!5e0!3m2!1sen!2sin!4v1735541967704!5m2!1sen!2sin" width="100%" height="350" loading="lazy"></iframe> */}
          </div>
          {membersList?.agencies?.map((item: any) => (
            <CollaborativeInfo
              key={item.agencyId}
              id={item.agencyId}
              groupId={membersList.groupId}
              data={item}
              onViewMap={
                item.profile.location &&
                item.profile.location !== "__private__" &&
                typeof item.profile.location === "object" &&
                "lat" in item.profile.location &&
                "lng" in item.profile.location
                  ? (onViewMap as ((agency: Agency) => void) | undefined)
                  : undefined
              }
            />
          ))}
          {perticularAgency && <ViewAgencyModelInfo isModalOpenSheltercc={isModalOpen} closeModalSheltercc={closeViewAgencyModel} agencyData={perticularAgency} groupId={membersList.groupId}  />}
        </div>
      </div>
    </>
  )
}

export default CollaborativeDetails;